import React, { useContext } from 'react';
import { TextInput } from '../../TextInput';
import { FormContext } from '../Form';
import { FastFormField, FormField } from '../FormField';
import styled from 'styled-components/macro';
import { inputSidePadding } from './inputStyles';

type FastInputFieldProps = {
  name: string;
  label: string | React.ReactNode;
  onBlur?: (newValue: string) => void;
  className?: string;
  testId?: string;
  isSmall?: boolean;
};

type InputFieldProps = {
  labelIcon?: React.ReactNode;
  onChange?: (newValue: string) => void;
  onInput?: () => void;
  disabled?: boolean;
  prefix?: string;
} & FastInputFieldProps;

export const TextInputField = (props: InputFieldProps) => {
  const formIsDisabled = useContext(FormContext).isDisabled;

  return (
    <StyledFormField name={props.name} label={props.label} labelIcon={props.labelIcon} className={props.className}>
      {({ field, form, $isValid, $isInvalid }) => (
        <>
          {props.prefix && <Prefix>{props.prefix}</Prefix>}
          <StyledTextInput
            {...field}
            data-testid={props.testId || textInputFieldTestId}
            $isValid={$isValid}
            $isInvalid={$isInvalid}
            $isSmall={props.isSmall}
            $hasPrefix={Boolean(props.prefix)}
            $prefixSize={(props.prefix?.length ?? 0) * 10 + 22}
            disabled={form.isSubmitting || formIsDisabled || props.disabled}
            onChange={changeEvent => {
              field.onChange(changeEvent);

              props.onChange?.(changeEvent.target.value);
            }}
            onBlur={blurEvent => {
              field.onBlur(blurEvent);
              props.onBlur?.(blurEvent.target.value);
            }}
            onInput={() => {
              props.onInput?.();
            }}
          />
        </>
      )}
    </StyledFormField>
  );
};

export const FastTextInputField = (props: FastInputFieldProps) => {
  const formIsDisabled = useContext(FormContext).isDisabled;

  return (
    <FastFormField name={props.name} label={props.label} className={props.className}>
      {({ field, form, $isValid, $isInvalid }) => (
        <TextInput
          {...field}
          data-testid={props.testId || textInputFieldTestId}
          $isValid={$isValid}
          $isInvalid={$isInvalid}
          disabled={form.isSubmitting || formIsDisabled}
          $isSmall={props.isSmall}
          onBlur={blurEvent => {
            field.onBlur(blurEvent);
            props.onBlur?.(blurEvent.target.value);
          }}
        />
      )}
    </FastFormField>
  );
};

export const textInputFieldTestId = 'text-input-field';

const StyledFormField = styled(FormField)`
  position: relative;
`;

const Prefix = styled.span`
  position: absolute;
  left: ${inputSidePadding};
`;

const StyledTextInput = styled(TextInput)<{ $hasPrefix: boolean; $prefixSize: number }>`
  padding-left: ${({ $hasPrefix, $prefixSize }) => ($hasPrefix ? `${$prefixSize}px` : inputSidePadding)};
`;
